import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuccessConfirmation = ({ formData, onReset }) => {
  useEffect(() => {
    // Auto-reset after 10 seconds
    const timer = setTimeout(() => {
      onReset();
    }, 10000);

    return () => clearTimeout(timer);
  }, [onReset]);

  const getServiceName = (serviceId) => {
    const serviceMap = {
      'web-development': '3D Web Development',
      'portfolio-design': 'Portfolio Design',
      'brand-identity': 'Brand Identity',
      'ui-ux-design': 'UI/UX Design',
      'creative-direction': 'Creative Direction',
      'consultation': 'Consultation',
      'workshop': 'Workshop/Speaking',
      'collaboration': 'Collaboration'
    };
    return serviceMap?.[serviceId] || serviceId;
  };

  const nextSteps = [
    {
      step: 1,
      title: 'Message Received',
      description: 'Your inquiry has been successfully submitted',
      icon: 'Check',
      status: 'completed'
    },
    {
      step: 2,
      title: 'Initial Review',
      description: 'I\'ll review your project details within 4-6 hours',
      icon: 'Eye',
      status: 'pending'
    },
    {
      step: 3,
      title: 'Personal Response',
      description: 'You\'ll receive a detailed response with next steps',
      icon: 'MessageSquare',
      status: 'upcoming'
    },
    {
      step: 4,
      title: 'Discovery Call',
      description: 'We\'ll schedule a call to discuss your project in detail',
      icon: 'Phone',
      status: 'upcoming'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-panel rounded-2xl p-8 max-w-2xl mx-auto text-center"
    >
      {/* Success Animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="w-20 h-20 bg-gradient-to-br from-success to-green-400 rounded-full flex items-center justify-center mx-auto mb-6 cosmic-glow"
      >
        <Icon name="Check" size={32} className="text-white" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h3 className="font-headline text-2xl text-foreground mb-2">Message Sent Successfully!</h3>
        <p className="font-body text-muted-foreground mb-8">
          Thank you for reaching out, {formData?.name}. I'm excited to learn more about your {getServiceName(formData?.service)?.toLowerCase()} project.
        </p>
      </motion.div>
      {/* Project Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="glass-panel-light rounded-lg p-6 mb-8 text-left"
      >
        <h4 className="font-headline text-lg text-foreground mb-4 flex items-center">
          <Icon name="FileText" size={20} className="mr-2 text-accent" />
          Project Summary
        </h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="font-body text-muted-foreground">Service:</span>
            <span className="font-body text-foreground">{getServiceName(formData?.service)}</span>
          </div>
          {formData?.budget && (
            <div className="flex justify-between">
              <span className="font-body text-muted-foreground">Budget:</span>
              <span className="font-body text-foreground">{formData?.budget}</span>
            </div>
          )}
          {formData?.timeline && (
            <div className="flex justify-between">
              <span className="font-body text-muted-foreground">Timeline:</span>
              <span className="font-body text-foreground">{formData?.timeline}</span>
            </div>
          )}
          {formData?.company && (
            <div className="flex justify-between">
              <span className="font-body text-muted-foreground">Company:</span>
              <span className="font-body text-foreground">{formData?.company}</span>
            </div>
          )}
        </div>
      </motion.div>
      {/* Next Steps */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mb-8"
      >
        <h4 className="font-headline text-lg text-foreground mb-6">What Happens Next</h4>
        <div className="space-y-4">
          {nextSteps?.map((step, index) => (
            <motion.div
              key={step?.step}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 + index * 0.1 }}
              className={`flex items-center space-x-4 p-4 rounded-lg ${
                step?.status === 'completed' 
                  ? 'bg-success/10 border border-success/20' 
                  : step?.status === 'pending' ?'bg-warning/10 border border-warning/20' :'bg-surface/30 border border-border'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step?.status === 'completed'
                  ? 'bg-success text-white'
                  : step?.status === 'pending' ?'bg-warning text-background' :'bg-surface text-muted-foreground'
              }`}>
                <Icon name={step?.icon} size={18} />
              </div>
              <div className="flex-1 text-left">
                <h5 className="font-body font-medium text-foreground">{step?.title}</h5>
                <p className="font-body text-sm text-muted-foreground">{step?.description}</p>
              </div>
              {step?.status === 'completed' && (
                <Icon name="CheckCircle" size={20} className="text-success" />
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
      {/* Contact Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4 }}
        className="glass-panel-light rounded-lg p-6 mb-8"
      >
        <h4 className="font-headline text-lg text-foreground mb-4">Need Immediate Assistance?</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={16} className="text-accent" />
            <span className="font-body text-sm text-foreground">ak253.com@gmail.com</span>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Phone" size={16} className="text-accent" />
            <span className="font-body text-sm text-foreground">+1 (555) 123-4567</span>
          </div>
        </div>
      </motion.div>
      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <Button
          variant="outline"
          onClick={onReset}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Send Another Message
        </Button>
        
        <Button
          variant="default"
          onClick={() => window.open('https://calendly.com/portfolio-consultation', '_blank')}
          iconName="Calendar"
          iconPosition="left"
          className="flex-1"
        >
          Schedule Call Now
        </Button>
      </motion.div>
      {/* Auto-reset indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="mt-6 pt-4 border-t border-border"
      >
        <p className="font-body text-xs text-muted-foreground">
          This form will reset automatically in 10 seconds
        </p>
      </motion.div>
    </motion.div>
  );
};

export default SuccessConfirmation;